/**
 * Display the game stats for a given game - this includes player stats and netminder stats
 */
import React from "react";
import GameEvent from "./GameEvent";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import ProfileImageContainer from "../core/ProfileImageContainer.js";
import Loading from "../core/Loading.js";
import { Link } from "react-router-dom";

const processStatsData = (jsonData, game_data) => {
  let processedData = {};

  processedData.home_stats = [];
  processedData.away_stats = [];

  processedData.home_nm_stats = [];
  processedData.away_nm_stats = [];

  try {
    jsonData.stats.forEach(function (stat) {
      if (stat.team_id === game_data.data.home_team.id) {
        processedData.home_stats.push(stat);
      } else if (stat.team_id === game_data.data.away_team.id) {
        processedData.away_stats.push(stat);
      }
    });

    jsonData.nm_stats.forEach(function (stat) {
      if (stat.team_id === game_data.data.home_team.id) {
        processedData.home_nm_stats.push(stat);
      } else if (stat.team_id === game_data.data.away_team.id) {
        processedData.away_nm_stats.push(stat);
      }
    });
  } catch (e) {
    console.error(e);
  }
  return processedData;
};

const GameRecap = (game_data) => {
  const { gameId } = useParams();

  const { isLoading, error, data } = useQuery({
    staleTime: 3600000,
    queryKey: ["game", gameId, "stats"],
    queryFn: () =>
      fetch(`${process.env.REACT_APP_BUIHA_API_URL}/game/${gameId}/stats/`)
        .then((res) => res.json())
        .then((json) => {
          return processStatsData(json, game_data);
        }),
  });

  let data2 = game_data.data;

  if (isLoading)
    return (
      <>
        <div className="flex flex-col w-full max-lg:justify-center max-lg:items-center lg:pr-2">
          <GameEvent eventName={data2.home_team.name} title={true} />
          <Loading />
        </div>
        <div className="flex flex-col w-full max-lg:justify-center max-lg:items-center lg:pl-2">
          <GameEvent eventName={data2.away_team.name} title={true} />
          <Loading />
        </div>
      </>
    );

  if (error) return "An error has occurred: " + error.message;

  return (
    // Container for all pages
    <>
      {/*h1 className="text-left text-2xl font-bold mb-2">Game Recap</h1>*/}
      <div className="flex flex-col w-full max-lg:justify-center max-lg:items-center lg:pr-2">
        <GameEvent eventName={data2.home_team.name} title={true} />

        <div className="flex flex-col w-full mb-2 sm:max-lg:px-16 pt-2 justify-center items-center">
          <div className="dark:bg-dark-4 bg-light-2 rounded px-1 pt-1 w-full">
            <table className="table-auto w-full mb-2">
              <thead>
                <tr>
                  <th className="text-center pb-1 max-xs:text-xs pl-1 font-normal">#</th>
                  <th
                    className="text-left pl-2 xs:pl-4 pb-1 max-xs:text-xs font-normal"
                    colSpan={2}
                  >
                    Name
                  </th>
                  <th className="text-center pb-1 xs:w-12 max-xs:text-xs font-normal">G</th>
                  <th className="text-center pb-1 xs:w-12 max-xs:text-xs font-normal">A</th>
                  <th className="text-center pb-1 xs:w-12 max-xs:text-xs font-normal">
                    PTS
                  </th>
                  <th className="text-center pb-1 xs:w-12 max-xs:text-xs font-normal">
                    PIM
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.home_stats.map(function (stat, index) {
                  return (
                    <tr
                      key={index}
                      className={
                        index % 2 === 0
                          ? "bg-light-1 dark:bg-dark-1"
                          : "bg-light-2 dark:bg-dark-4"
                      }
                    >
                      <td className="text-center pt-1 text-darken font-light pb-1 pl-1 max-xs:text-xs">
                        {stat.jersey_number}
                      </td>
                      <td className="text-center pl-2 xs:pl-4 xs:w-10">
                        <div className="h-6 w-6 xs:mr-2 rounded-full bg-light-1 dark:bg-dark-2 flex flex-col justify-center items-center">
                          <ProfileImageContainer
                            player={stat.player_id}
                            club={data2.home_team.club}
                            backup={data2.home_team.profile}
                            alt={stat.player_name}
                            extraClasses={"rounded-full"}
                          />
                        </div>
                      </td>
                      <td className="text-left max-xs:text-xs">
                        <Link
                          to={`/player/${stat.player_id}`}
                          className="transition-colors duration-300 ease-in-out hover:text-buiha-red dark:hover:text-dark-5"
                        >
                          {stat.player_name}
                        </Link>
                      </td>
                      <td className="text-center max-xs:text-xs font-light">
                        {stat.goals}
                      </td>
                      <td className="text-center max-xs:text-xs font-light">
                        {stat.assists}
                      </td>
                      <td className="text-center max-xs:text-xs font-light">
                        {stat.points}
                      </td>
                      <td className="text-center max-xs:text-xs font-light">{stat.pim}</td>
                    </tr>
                  );
                })}
              </tbody>
              <thead>
                <tr>
                  <th className="text-center pb-1 pt-3 max-xs:text-xs pl-1 font-normal">
                    #
                  </th>
                  <th
                    className="text-left pl-4 pb-1 pt-3 max-xs:text-xs font-normal"
                    colSpan={2}
                  >
                    Netminder
                  </th>
                  <th className="text-center pb-1 pt-3 xs:w-12 max-xs:text-xs font-normal">
                    MIN
                  </th>
                  <th className="text-center pb-1 pt-3 xs:w-12 max-xs:text-xs font-normal">
                    SA
                  </th>
                  <th className="text-center pb-1 pt-3 xs:w-12 max-xs:text-xs font-normal">
                    GA
                  </th>
                  <th className="text-center pb-1 pt-3 xs:w-12 max-xs:text-xs font-normal">
                    SV%
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.home_nm_stats.map(function (stat, index) {
                  return (
                    <tr
                      key={index}
                      className={
                        index % 2 === 0
                          ? "bg-light-1 dark:bg-dark-1"
                          : "bg-light-2 dark:bg-dark-4"
                      }
                    >
                      <td className="text-center pt-1 text-darken font-light pb-1 pl-1 max-xs:text-xs">
                        {stat.jersey_number}
                      </td>
                      <td className="text-center pl-2 xs:pl-4 w-10">
                        <div className="h-6 w-6 mr-2 rounded-full bg-light-1 dark:bg-dark-2 flex flex-col justify-center items-center">
                          <ProfileImageContainer
                            player={stat.player_id}
                            club={data2.home_team.club}
                            backup={data2.home_team.profile}
                            alt={stat.player_name}
                            extraClasses={"rounded-full"}
                          />
                        </div>
                      </td>
                      <td className="text-left max-xs:text-xs">
                        <Link
                          to={`/player/${stat.player_id}`}
                          className="transition-colors duration-300 ease-in-out hover:text-buiha-red dark:hover:text-dark-5"
                        >
                          {stat.player_name}
                        </Link>
                      </td>
                      <td className="text-center max-xs:text-xs font-light">
                        {/*{stat.seconds > 0 ? (stat.seconds / 60).toFixed(2) : "-"}*/}
                        {stat.seconds > 0
                          ? String(~~(stat.seconds / 60)).padStart(2, "0") +
                            ":" +
                            String(stat.seconds % 60).padStart(2, "0")
                          : "-"}
                      </td>
                      <td className="text-center max-xs:text-xs font-light">
                        {stat.seconds > 0 ? stat.shots_against : "-"}
                      </td>
                      <td className="text-center max-xs:text-xs font-light">
                        {stat.seconds > 0 ? stat.goals_against : "-"}
                      </td>
                      <td className="text-center max-xs:text-xs font-light">
                        {stat.seconds > 0
                          ? (stat.sav_pct ? stat.sav_pct : 0).toFixed(2)
                          : "-"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* TODO Fix this bad code */}
      <div className="flex flex-col w-full max-lg:justify-center max-lg:items-center lg:pl-2">
        <GameEvent eventName={data2.away_team.name} title={true} />

        <div className="flex flex-col w-full mb-2 sm:max-lg:px-16 pt-2 justify-center items-center">
          <div className="dark:bg-dark-4 bg-light-2 rounded px-1 pt-1 w-full">
            <table className="table-auto w-full mb-2">
              <thead>
                <tr>
                  <th className="text-center pb-1 max-xs:text-xs pl-1 font-normal">#</th>
                  <th
                    className="text-left pl-2 xs:pl-4 pb-1 max-xs:text-xs font-normal"
                    colSpan={2}
                  >
                    Name
                  </th>
                  <th className="text-center pb-1 xs:w-12 max-xs:text-xs font-normal">G</th>
                  <th className="text-center pb-1 xs:w-12 max-xs:text-xs font-normal">A</th>
                  <th className="text-center pb-1 xs:w-12 max-xs:text-xs font-normal">
                    PTS
                  </th>
                  <th className="text-center pb-1 xs:w-12 max-xs:text-xs font-normal">
                    PIM
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.away_stats.map(function (stat, index) {
                  return (
                    <tr
                      key={index}
                      className={
                        index % 2 === 0
                          ? "bg-light-1 dark:bg-dark-1"
                          : "bg-light-2 dark:bg-dark-4"
                      }
                    >
                      <td className="text-center pt-1 text-darken font-light pb-1 max-xs:text-xs pl-1">
                        {stat.jersey_number}
                      </td>
                      <td className="text-center pl-2 xs:pl-4 w-10">
                        <div className="h-6 w-6 mr-2 rounded-full bg-light-1 dark:bg-dark-2 flex flex-col justify-center items-center">
                          <ProfileImageContainer
                            player={stat.player_id}
                            club={data2.away_team.club}
                            backup={data2.away_team.profile}
                            alt={stat.player_name}
                            extraClasses={"rounded-full"}
                          />
                        </div>
                      </td>
                      <td className="text-left max-xs:text-xs">
                        <Link
                          to={`/player/${stat.player_id}`}
                          className="transition-colors duration-300 ease-in-out hover:text-buiha-red dark:hover:text-dark-5"
                        >
                          {stat.player_name}
                        </Link>
                      </td>
                      <td className="text-center max-xs:text-xs font-light">
                        {stat.goals}
                      </td>
                      <td className="text-center max-xs:text-xs font-light">
                        {stat.assists}
                      </td>
                      <td className="text-center max-xs:text-xs font-light">
                        {stat.points}
                      </td>
                      <td className="text-center max-xs:text-xs font-light">{stat.pim}</td>
                    </tr>
                  );
                })}
              </tbody>
              <thead>
                <tr>
                  <th className="text-center pb-1 pt-3 max-xs:text-xs pl-1 font-normal">
                    #
                  </th>
                  <th
                    className="text-left pl-4 pb-1 pt-3 max-xs:text-xs font-normal"
                    colSpan={2}
                  >
                    Name
                  </th>
                  <th className="text-center pb-1 pt-3 xs:w-12 max-xs:text-xs font-normal">
                    MIN
                  </th>
                  <th className="text-center pb-1 pt-3 xs:w-12 max-xs:text-xs font-normal">
                    SA
                  </th>
                  <th className="text-center pb-1 pt-3 xs:w-12 max-xs:text-xs font-normal">
                    GA
                  </th>
                  <th className="text-center pb-1 pt-3 xs:w-12 max-xs:text-xs font-normal">
                    SV%
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.away_nm_stats.map(function (stat, index) {
                  return (
                    <tr
                      key={index}
                      className={
                        index % 2 === 0
                          ? "bg-light-1 dark:bg-dark-1"
                          : "bg-light-2 dark:bg-dark-4"
                      }
                    >
                      <td className="text-center pt-1 text-darken font-light pb-1 max-xs:text-xs pl-1">
                        {stat.jersey_number}
                      </td>
                      <td className="text-center pl-2 xs:pl-4 w-10">
                        <div className="h-6 w-6 mr-2 rounded-full bg-light-1 dark:bg-dark-2 flex flex-col justify-center items-center">
                          <ProfileImageContainer
                            player={stat.player_id}
                            club={data2.away_team.club}
                            backup={data2.away_team.profile}
                            alt={stat.player_name}
                            extraClasses={"rounded-full"}
                          />
                        </div>
                      </td>
                      <td className="text-left max-xs:text-xs">
                        <Link
                          to={`/player/${stat.player_id}`}
                          className="transition-colors duration-300 ease-in-out hover:text-buiha-red dark:hover:text-dark-5"
                        >
                          {stat.player_name}
                        </Link>
                      </td>
                      <td className="text-center max-xs:text-xs font-light">
                        {stat.seconds > 0
                          ? String(~~(stat.seconds / 60)).padStart(2, "0") +
                            ":" +
                            String(stat.seconds % 60).padStart(2, "0")
                          : "-"}
                      </td>
                      <td className="text-center max-xs:text-xs font-light">
                        {stat.seconds > 0 ? stat.shots_against : "-"}
                      </td>
                      <td className="text-center max-xs:text-xs font-light">
                        {stat.seconds > 0 ? stat.goals_against : "-"}
                      </td>
                      <td className="text-center max-xs:text-xs font-light">
                        {stat.seconds > 0
                          ? (stat.sav_pct ? stat.sav_pct : 0).toFixed(2)
                          : "-"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
};
export default GameRecap;
